import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Pencil, Plus, Trash2, X } from 'lucide-react';
import { resource } from '../services/http';
import { shareholders as shareholderApi } from '../services/companyDetailApi';
import { Deal } from '../types/deal';
import { won } from '../types/vehicle';
import { todayLocal } from '../lib/date';
import { DealBreach, DealTerms, addYears, baseDate, lines, redemptionAmount, termsEnded } from '../lib/terms';
import { ConfirmButton } from './ConfirmButton';

const termsApi = resource<DealTerms>('deal-terms');
const breachApi = resource<DealBreach>('deal-breaches');

type Form = Omit<DealTerms, 'id' | 'dealId'>;
const EMPTY: Form = {
  effectiveOn: null, durationYears: null, dividendParticipating: null, dividendCumulative: null, dividendRate: null,
  redemptionAfterYears: null, redemptionRate: null, conversionRatio: null, conversionAfterYears: null, conversionPrice: null,
  refixing: '', consultItems: '', breachLimit: null, putRate: null, penaltyPct: null, lateRate: null,
  annualReportDays: null, terminateBelowPct: null, terminateOnIpo: true, documentId: null, note: '',
};

/**
 * 투자 건 상세 — 계약조건 (#70). 투자계약서 · 투자조건 요약에서 옮겨 적는다.
 * 계산(상환 · 전환 가능일, 오늘 상환 금액, 계약 종료)은 lib/terms.ts.
 */
export const DealTermsSection: React.FC<{
  deal: Deal; listed: boolean; invested: number; dividends: number;
}> = ({ deal, listed, invested, dividends }) => {
  const [terms, setTerms] = useState<DealTerms | null>(null);
  const [breaches, setBreaches] = useState<DealBreach[]>([]);
  const [stakePct, setStakePct] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [breachOpen, setBreachOpen] = useState(false);

  const load = useCallback(async () => {
    const [t, b, holders] = await Promise.all([
      termsApi.list({ dealId: deal.id }), breachApi.list({ dealId: deal.id }), shareholderApi.list({ companyId: deal.companyId }),
    ]);
    setTerms(t[0] ?? null);
    setBreaches(b.sort((x, y) => y.occurredOn.localeCompare(x.occurredOn)));
    // 지분 — 가장 최근 주주명부의 합을 발행주식으로 본다
    const last = holders.reduce((m, h) => (h.asOf > m ? h.asOf : m), '');
    const total = holders.filter((h) => h.asOf === last).reduce((s, h) => s + Number(h.shares), 0);
    setStakePct(deal.shares && total > 0 ? (deal.shares / total) * 100 : null);
  }, [deal.id, deal.companyId, deal.shares]);

  useEffect(() => { load(); }, [load]);

  const today = todayLocal();
  const base = terms ? baseDate(terms, deal) : null;
  const redeemFrom = terms && base && terms.redemptionAfterYears !== null ? addYears(base, terms.redemptionAfterYears) : null;
  const convertFrom = terms && base && terms.conversionAfterYears !== null ? addYears(base, terms.conversionAfterYears) : null;
  const expires = terms && base && terms.durationYears !== null ? addYears(base, terms.durationYears) : null;
  const ended = terms ? termsEnded(terms, listed, stakePct) : null;
  const overLimit = terms?.breachLimit != null && breaches.length >= terms.breachLimit;
  // 상환할 수 있는 날이 아직이면 그날 기준으로 — 오늘 금액은 청구할 수 없는 값이다
  const redeemOn = redeemFrom && redeemFrom > today ? redeemFrom : today;
  const redeemNow = terms && base && terms.redemptionRate !== null && invested > 0
    ? redemptionAmount(invested, terms.redemptionRate, base, redeemOn, dividends) : null;

  const dateCell = (d: string | null) => (d ? <span className="tabular-nums">{d.replace(/-/g, '.')}{d <= today && <span className="ml-1 text-emerald-700 font-medium">가능</span>}</span> : '—');
  const pct = (v: number | null) => (v === null ? '—' : `${v}%`);

  return (
    <section data-tour="terms" className="mt-4 bg-white border border-slate-200 rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">계약조건</h2>
        <button onClick={() => setEditing(true)}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50">
          {terms ? <><Pencil size={11} /> 고치기</> : <><Plus size={11} /> 입력</>}
        </button>
      </div>

      {!terms ? (
        <p className="py-8 text-center text-sm text-slate-500">아직 계약조건을 적지 않았습니다. 투자계약서 · 투자조건 요약을 보고 옮겨 적습니다.</p>
      ) : (
        <div className="px-4 py-3 space-y-3 text-sm">
          {(ended || overLimit) && (
            <div className="space-y-1">
              {ended && <p className="flex items-start gap-1.5 text-xs text-amber-700"><AlertTriangle size={13} className="mt-0.5 flex-shrink-0" /> 계약 종료 조건에 닿았습니다 — {ended}. 사전협의 · 보고 의무가 살아 있는지 확인하세요.</p>}
              {overLimit && <p className="flex items-start gap-1.5 text-xs text-red-700"><AlertTriangle size={13} className="mt-0.5 flex-shrink-0" /> 사전협의 위반 {breaches.length}회 — 계약상 {terms.breachLimit}회면 주식매수청구 사유입니다.</p>}
            </div>
          )}

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
            {([
              ['기산일', base ? base.replace(/-/g, '.') : '—'],
              ['상환청구', dateCell(redeemFrom)],
              ['전환청구', dateCell(convertFrom)],
              ['존속기간 끝', expires ? expires.replace(/-/g, '.') : '—'],
              ['상환 이율', terms.redemptionRate === null ? '—' : `연복리 ${terms.redemptionRate}%`],
              ['전환', terms.conversionRatio === null ? '—' : `1 : ${terms.conversionRatio}${terms.conversionPrice ? ` · ${won(terms.conversionPrice)}` : ''}`],
              ['배당', [terms.dividendParticipating ? '참가적' : terms.dividendParticipating === false ? '비참가적' : '', terms.dividendCumulative ? '누적적' : terms.dividendCumulative === false ? '비누적적' : '', terms.dividendRate !== null ? `${terms.dividendRate}%` : ''].filter(Boolean).join(' · ') || '—'],
              ['지분', stakePct === null ? '—' : `${stakePct.toFixed(2)}%`],
              ['매수청구 이율', pct(terms.putRate)],
              ['위약벌', terms.penaltyPct === null ? '—' : `투자금의 ${terms.penaltyPct}%`],
              ['지연배상', pct(terms.lateRate)],
              ['연차보고', terms.annualReportDays === null ? '—' : `회계연도 시작 후 ${terms.annualReportDays}일`],
            ] as [string, React.ReactNode][]).map(([k, v]) => (
              <div key={k}><dt className="text-xs text-slate-400">{k}</dt><dd className="text-slate-900">{v}</dd></div>
            ))}
          </dl>

          {redeemNow !== null && (
            <p className="text-xs text-slate-500">
              {redeemOn === today ? '오늘 상환하면' : `상환청구 첫날(${redeemOn.replace(/-/g, '.')})에 상환하면`} 약 <b className="text-slate-900 tabular-nums">{won(redeemNow)}</b>
              <span className="text-slate-400"> — 투자 {won(invested)} × 연복리 {terms.redemptionRate}%{dividends > 0 ? ` − 받은 배당 ${won(dividends)}` : ''}. 참고 계산(계약의 산식을 확인)</span>
            </p>
          )}
          <p className="text-xs text-slate-400">
            계약 종료: {terms.terminateBelowPct !== null ? `지분 ${terms.terminateBelowPct}% 미만` : ''}{terms.terminateBelowPct !== null && terms.terminateOnIpo ? ' 또는 ' : ''}{terms.terminateOnIpo ? '상장' : ''}{terms.terminateBelowPct === null && !terms.terminateOnIpo ? '조건 없음' : ''}
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <TextList title="전환가격 조정" text={terms.refixing} />
            <TextList title="사전협의 항목" text={terms.consultItems} />
          </div>
          {terms.note && <p className="text-xs text-slate-500">{terms.note}</p>}

          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="text-xs font-semibold text-slate-700">
                사전협의 없이 진행한 일 <span className="font-normal text-slate-400">{breaches.length}회{terms.breachLimit ? ` / 계약상 ${terms.breachLimit}회` : ''}</span>
              </h3>
              <button onClick={() => setBreachOpen(true)} aria-label="사전협의 위반 기록" className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"><Plus size={11} /> 기록</button>
            </div>
            {breaches.length === 0 ? <p className="text-xs text-slate-400">없음</p> : (
              <ul className="space-y-1">
                {breaches.map((b) => (
                  <li key={b.id} className="flex items-center gap-2 text-xs">
                    <span className="tabular-nums text-slate-500">{b.occurredOn.replace(/-/g, '.')}</span>
                    <span className="text-slate-900">{b.item}</span>
                    {b.note && <span className="text-slate-400 truncate">{b.note}</span>}
                    <ConfirmButton ask="한 번 더 누르면 지웁니다" className="ml-auto text-slate-300 hover:text-red-600"
                      onConfirm={async () => { await breachApi.remove(b.id); load(); }}><Trash2 size={12} /></ConfirmButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {editing && <TermsModal dealId={deal.id} terms={terms} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {breachOpen && <BreachModal dealId={deal.id} items={terms ? lines(terms.consultItems) : []} onClose={() => setBreachOpen(false)} onSaved={() => { setBreachOpen(false); load(); }} />}
    </section>
  );
};

const TextList: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <div>
    <h3 className="text-xs font-semibold text-slate-700 mb-1">{title}</h3>
    {lines(text).length === 0 ? <p className="text-xs text-slate-400">—</p> : (
      <ol className="list-decimal pl-4 space-y-0.5 text-xs text-slate-700">{lines(text).map((l, i) => <li key={i}>{l}</li>)}</ol>
    )}
  </div>
);

const input = 'w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
const label = 'block text-xs font-medium text-slate-500 mb-1';

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; foot: React.ReactNode; wide?: boolean }> = ({ title, onClose, children, foot, wide }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto">
    <div className={`bg-white rounded-lg border border-slate-200 shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} my-8`}>
      <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <button onClick={onClose} aria-label="닫기" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
      <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">{foot}</div>
    </div>
  </div>
);

const TermsModal: React.FC<{ dealId: number; terms: DealTerms | null; onClose: () => void; onSaved: () => void }> = ({ dealId, terms, onClose, onSaved }) => {
  const [f, setF] = useState<Form>(() => (terms ? (({ id, dealId: _d, ...rest }) => rest)(terms) : EMPTY));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const num = (k: keyof Form, text: string, step = 'any') => (
    <div>
      <label className={label} htmlFor={`t-${k}`}>{text}</label>
      <input id={`t-${k}`} type="number" step={step} min={0} className={input + ' text-right'} value={(f[k] as number | null) ?? ''}
        onChange={(e) => set(k, (e.target.value === '' ? null : Number(e.target.value)) as never)} />
    </div>
  );
  const tri = (k: 'dividendParticipating' | 'dividendCumulative', yes: string, no: string) => (
    <div>
      <label className={label} htmlFor={`t-${k}`}>{yes} 여부</label>
      <select id={`t-${k}`} className={input} value={f[k] === null ? '' : f[k] ? 'y' : 'n'}
        onChange={(e) => set(k, e.target.value === '' ? null : e.target.value === 'y')}>
        <option value="">모름</option><option value="y">{yes}</option><option value="n">{no}</option>
      </select>
    </div>
  );
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const body = { ...f, refixing: f.refixing.trim(), consultItems: f.consultItems.trim(), note: f.note.trim() };
      if (terms) await termsApi.update(terms.id, body); else await termsApi.create({ ...body, dealId });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };
  return (
    <Modal wide title="계약조건" onClose={onClose} foot={<>
      {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
      <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
      <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">{saving ? '저장 중...' : '저장'}</button>
    </>}>
      <p className="text-xs text-slate-400">모르는 칸은 비워 둡니다. 이율 · 비율은 % 숫자만(8% → 8).</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className={label} htmlFor="t-eff">효력발생일</label>
          <input id="t-eff" type="date" className={input} value={f.effectiveOn ?? ''} onChange={(e) => set('effectiveOn', e.target.value || null)} />
        </div>
        {num('durationYears', '존속기간(년)', '1')}
        {num('redemptionAfterYears', '상환 — 몇 년 뒤', '0.5')}
        {num('redemptionRate', '상환 이율(연복리 %)')}
        {num('conversionRatio', '전환비율(1주당)')}
        {num('conversionAfterYears', '전환 — 몇 년 뒤', '0.5')}
        {num('conversionPrice', '전환가격(원)')}
        {num('dividendRate', '우선배당률(%)')}
        {tri('dividendParticipating', '참가적', '비참가적')}
        {tri('dividendCumulative', '누적적', '비누적적')}
        {num('breachLimit', '위반 몇 회면 매수청구', '1')}
        {num('putRate', '매수청구 이율(%)')}
        {num('penaltyPct', '위약벌(투자금 %)')}
        {num('lateRate', '지연배상(%)')}
        {num('annualReportDays', '연차보고(일)', '1')}
        {num('terminateBelowPct', '종료 — 지분 % 미만')}
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={f.terminateOnIpo} onChange={(e) => set('terminateOnIpo', e.target.checked)} /> 상장하면 협의권 · 보고 조항이 끝난다
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="t-refix">전환가격 조정 — 줄마다 하나</label>
          <textarea id="t-refix" rows={4} className={input} value={f.refixing} onChange={(e) => set('refixing', e.target.value)} />
        </div>
        <div>
          <label className={label} htmlFor="t-consult">사전협의 항목 — 줄마다 하나</label>
          <textarea id="t-consult" rows={4} className={input} value={f.consultItems} onChange={(e) => set('consultItems', e.target.value)} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="t-note">메모</label>
        <input id="t-note" className={input} value={f.note} onChange={(e) => set('note', e.target.value)} />
      </div>
    </Modal>
  );
};

const BreachModal: React.FC<{ dealId: number; items: string[]; onClose: () => void; onSaved: () => void }> = ({ dealId, items, onClose, onSaved }) => {
  const [occurredOn, setOn] = useState(todayLocal());
  const [item, setItem] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true); setErr(null);
    try { await breachApi.create({ dealId, occurredOn, item: item.trim(), note: note.trim() }); onSaved(); }
    catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };
  return (
    <Modal title="사전협의 없이 진행한 일" onClose={onClose} foot={<>
      {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
      <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
      <button onClick={save} disabled={!item.trim() || saving} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">{saving ? '저장 중...' : '기록'}</button>
    </>}>
      <div>
        <label className={label} htmlFor="b-on">진행한 날</label>
        <input id="b-on" type="date" max={todayLocal()} className={input} value={occurredOn} onChange={(e) => setOn(e.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor="b-item">무엇을</label>
        <input id="b-item" list="b-items" className={input} value={item} onChange={(e) => setItem(e.target.value)} placeholder="사전협의 항목" />
        <datalist id="b-items">{items.map((x) => <option key={x} value={x} />)}</datalist>
      </div>
      <div>
        <label className={label} htmlFor="b-note">메모</label>
        <input id="b-note" className={input} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
};
